import type { GigawattAgentState } from '@/types/aiChat';
import { generateAiPageMap } from '@/helpers/routeFormatters';
import type { ShoppingCountryValue } from '@/redux/slices/storefrontContextSlice';

/**
 * Maps route paths to semantic page identifiers matching navigation tool enum
 * AUTO-GENERATED from ROUTE_DEFINITIONS via generateAiPageMap()
 * The AI already understands these semantic names from the navigation tool
 *
 * Note: This inverts the map (path -> displayName instead of displayName -> path)
 */
const PAGE_ROUTE_MAP: Record<string, string> = (() => {
  const pageMap = generateAiPageMap(); // displayName -> path
  const inverted: Record<string, string> = {};
  for (const [displayName, path] of Object.entries(pageMap)) {
    inverted[path] = displayName;
  }
  return inverted;
})();

/**
 * Derives semantic page identifier from current path
 * Matches exact paths first, then checks for detail page patterns
 * @param path - Current browser path from React Router
 * @returns Semantic identifier (e.g., "dashboard", "employee_detail", "unknown_page")
 */
export const getSemanticPageIdentifier = (path: string): string => {
  // Remove trailing slash for consistent matching
  const normalizedPath = path.endsWith('/') ? path.slice(0, -1) : path;

  // Exact match
  if (PAGE_ROUTE_MAP[normalizedPath]) {
    return PAGE_ROUTE_MAP[normalizedPath];
  }

  // Pattern match for detail pages (e.g., /employees/123 -> people_detail)
  // We check if the path starts with a known base path and has additional segments
  for (const [route, identifier] of Object.entries(PAGE_ROUTE_MAP)) {
    if (normalizedPath.startsWith(route + '/')) {
      // Return base identifier with _detail suffix for detail pages
      return `${identifier}_detail`;
    }
  }

  return 'unknown_page';
};

/**
 * Configuration object for building agent state
 * All data should be extracted/computed before calling buildAgentState
 */
export type AgentStateConfig = {
  // User context (mostly static)
  userName?: string;
  employeeId?: string;
  timezone: string;
  language: string;
  // Location context (updates on navigation)
  currentPath: string;
  currentPage: string;
  // Storefront context (updates frequently — mirrors the shopping country selector)
  storefrontShoppingCountry?: ShoppingCountryValue;
  // Conversation state (updates frequently)
  lastToolset: string | null;
};

/**
 * Builds agent state structure from provided configuration
 *
 * This is a PURE structuring function - it does NOT:
 * - Extract data from Redux/Router/Browser
 * - Compute derived values
 * - Make any side effects
 *
 * It ONLY defines the structure of the agent state object.
 *
 * All data extraction and computation should happen in useAgentState hook.
 *
 * @param config - Pre-computed configuration object with all required data
 * @returns Structured state object ready for CopilotKit agent
 */
export const buildAgentState = (
  config: AgentStateConfig
): GigawattAgentState => {
  const state: GigawattAgentState = {
    user: {
      timezone: config.timezone,
      language: config.language,
      ...(config.userName && { userName: config.userName }),
      ...(config.employeeId && { employeeId: config.employeeId }),
    },
    context: {
      currentPage: config.currentPage,
      currentPath: config.currentPath,
      ...(config.storefrontShoppingCountry && {
        storefrontShoppingCountry: config.storefrontShoppingCountry,
      }),
    },
    lastToolset: config.lastToolset,
  };

  return state;
};
